import React, { useState, useEffect } from "react";
import { SpecialZoomLevel, Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const PDFViewer = ({ pdfUrl }) => {
  // Create the default layout plugin instance
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // State to track loading errors
  const [error, setError] = useState(null);
  const [viewerKey, setViewerKey] = useState(Date.now());

  // Reset viewer when URL changes
  useEffect(() => {
    if (pdfUrl) {
      setViewerKey(Date.now());
      setError(null);
    }
  }, [pdfUrl]);

  // Handler for when the document fails to load
  const handleDocumentLoadError = (err) => {
    console.error("Failed to load PDF document:", err);
    setError(
      "Failed to load the PDF document. Please check if the file exists and try again."
    );
  };

  // Add a useEffect to hide the buttons after the component mounts
  useEffect(() => {
    // Function to hide specific buttons by their CSS selectors
    const hideButtons = () => {
      // Target the specific buttons by their attributes
      // These selectors may need adjustment based on your exact PDF.js version
      const buttonSelectors = [
        'button[data-testid="open__button"]', // Open/Upload button
        'button[data-testid="get-file__download-button"]', // Download button
        'button[data-testid="print__button"]', // Print button
      ];

      // Try to find and hide each button
      buttonSelectors.forEach((selector) => {
        const buttons = document.querySelectorAll(selector);
        buttons.forEach((button) => {
          if (button && button.parentElement) {
            button.parentElement.style.display = "none";
          }
        });
      });
    };

    // Execute initially and set an interval to handle dynamic content loading
    if (pdfUrl) {
      // Initial delay to let the PDF viewer render
      setTimeout(hideButtons, 300);

      // Set up an interval to repeatedly try to hide the buttons
      // This handles cases where the viewer components load asynchronously
      const intervalId = setInterval(hideButtons, 1000);

      // Clear the interval when component unmounts
      return () => clearInterval(intervalId);
    }
  }, [pdfUrl, viewerKey]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // Prevent outer scrollbar
      }}
    >
      {error ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#d32f2f",
          }}
        >
          {error}
        </div>
      ) : pdfUrl ? (
        <div
          style={{
            flex: 1,
            overflow: "hidden", // Prevent container scrollbar
            position: "relative",
            width: "100%",
          }}
        >
          {/* Updated Worker URL to match the API version */}
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <Viewer
                key={viewerKey}
                fileUrl={pdfUrl}
                plugins={[defaultLayoutPluginInstance]}
                onDocumentLoadError={handleDocumentLoadError}
                defaultScale={SpecialZoomLevel.PageFit}
              />
            </div>
          </Worker>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <p>No document selected</p>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
